import React, { useState } from 'react';
import Chart from 'react-apexcharts';
import 'rc-dropdown/assets/index.css';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import userIcon from 'assets/icons/user-light.svg';
import { TRANSLATIONS } from 'types/enums';
import { useCubeQuery } from '@cubejs-client/react';

import './IncidentsChart.scss';
import LoadingSpinner from 'components/LoadingSpinner';
import { set, subDays } from 'date-fns/esm';
import ChartDropdown from 'components/Dropdown/ChartDropdown';
import { lastDayOfMonth } from 'date-fns/esm/fp';
import ChartTabs from 'components/Tabs/ChartTabs';
import mean from 'lodash.mean';
import sum from 'lodash.sum';
import { useSelector } from 'react-redux';
import { selectLocationsToAccess } from 'store/selectors/globalFilters.selectors';
import EmptyState from 'components/EmptyState/EmptyState';

const IncidentsChart = () => {
  const { t } = useTranslation();

  const dropDownItems = [{
    slug: 'last-7-days',
    label: t(TRANSLATIONS.LAST_7_DAYS),
  }, {
    slug: 'last-30-days',
    label: t(TRANSLATIONS.LAST_30_DAYS),
  }, {
    slug: 'month-to-date',
    label: t(TRANSLATIONS.MONTH_TO_DATE),
  }];

  const [selectedDropDownItem, setSelectedDropDownItem] = useState(dropDownItems[0]);
  const [dropDownOpen, setDropDownOpen] = useState(false);

  const locationsToAccess = useSelector(selectLocationsToAccess);
  const skip = !locationsToAccess.length;

  let chartApiResponse = null as any;

  const getDateRange = () => {
    const currentDate = new Date();
    let filter = ['', ''] as any[];
    if (selectedDropDownItem.slug === 'last-7-days') {
      filter = [subDays(currentDate, 14), currentDate];
    }
    if (selectedDropDownItem.slug === 'last-30-days') {
      filter = [subDays(currentDate, 60), currentDate];
    }
    if (selectedDropDownItem.slug === 'month-to-date') {
      const lastMonthDay = lastDayOfMonth(currentDate);
      const firstDayLastMonth = set(subDays(lastMonthDay, 50), { date: 1 });
      filter = [firstDayLastMonth, lastMonthDay];
    }
    return [format(filter[0], 'yyyy-MM-dd'), format(filter[1], 'yyyy-MM-dd')];
  };

  const getDateRangeCurrent = () => {
    const currentDate = new Date();
    let filter = ['', ''] as any[];
    if (selectedDropDownItem.slug === 'last-7-days') {
      filter = [subDays(currentDate, 7), currentDate];
    }
    if (selectedDropDownItem.slug === 'last-30-days') {
      filter = [subDays(currentDate, 30), currentDate];
    }
    if (selectedDropDownItem.slug === 'month-to-date') {
      const lastMonthDay = lastDayOfMonth(currentDate);
      const firstDayLastMonth = set(lastMonthDay, { date: 1 });
      filter = [firstDayLastMonth, lastMonthDay];
    }
    return [format(filter[0], 'yyyy-MM-dd'), format(filter[1], 'yyyy-MM-dd')];
  };

  const getGranularity = (): 'day' => {
    return 'day';
  };

  const dateRange = getDateRange() as unknown as string[];
  const currentDateRange = getDateRangeCurrent() as unknown as string[];
  const granularity = getGranularity();

  const { resultSet: countResult, isLoading } = useCubeQuery({
    measures: ['Incidents.count'],
    timeDimensions: [{
      dimension: 'Incidents.incidentdatetime',
      granularity,
      dateRange,
    }],
    order: {
      'Incidents.incidentdatetime': 'asc',
    },
    filters: [{
      dimension: 'Incidents.locationId',
      operator: 'equals',
      values: locationsToAccess,
    }],
  }, { skip });
  const { resultSet: uniqueCount } = useCubeQuery({
    measures: ['Incidents.uniqueReporters'],
    timeDimensions: [{
      dimension: 'Incidents.incidentdatetime',
      granularity,
      dateRange,
    }],
    order: {
      'Incidents.incidentdatetime': 'asc',
    },
    filters: [{
      dimension: 'Incidents.locationId',
      operator: 'equals',
      values: locationsToAccess,
    }],
  }, { skip });
  const { resultSet: uniqueReportersQ }: { resultSet: any } = useCubeQuery({
    measures: ['Incidents.uniqueReporters'],
    timeDimensions: [{
      dimension: 'Incidents.incidentdatetime',
      dateRange: currentDateRange,
    }],
    filters: [{
      dimension: 'Incidents.locationId',
      operator: 'equals',
      values: locationsToAccess,
    }],
  }, { skip });
  const { resultSet: headCount }: { resultSet: any } = useCubeQuery({
    measures: ['Users.belongsToLocation'],
    filters: [{
      dimension: 'Users.locationId',
      operator: 'equals',
      values: locationsToAccess,
    }],
  }, { skip });

  if (countResult?.series()[0] && uniqueCount) {
    const incidents = countResult.series()[0];
    const uniqueU = uniqueCount.series()[0];
    const uniqueReporters = uniqueReportersQ?.rawData()[0]['Incidents.uniqueReporters'];

    const midOfSeries = Math.floor(incidents.series.length / 2);
    const incidentsData = incidents.series.map((v: any) => v.value);
    const uniqueReportersData = uniqueU.series.map((v: any) => v.value);
    const avgPerUserData = incidents.series.map(
      (v: any, i: number) => ((v.value / uniqueU.series[i].value) || 0),
    );

    chartApiResponse = {
      totalReports: sum(incidentsData.slice(midOfSeries)),
      uniqueReporters,
      averageReportsByUser: mean(avgPerUserData.slice(midOfSeries)).toFixed(1),
      totalUsers: headCount?.rawData()[0]['Users.belongsToLocation'], // Total users on the DB. Explained more below.
      incidentsByTime: {
        data: incidentsData,
        labels: incidents.series.map((v: any) => v.x),
      },
      uniqueReportersByTime: {
        data: uniqueReportersData,
        labels: uniqueU.series.map((v: any) => v.x),
      },
      avgPerUser: {
        data: avgPerUserData,
        labels: uniqueU.series.map((v: any) => v.x),
      },
    };
  }

  // @ts-ignore
  const handleDropDownChange = ({ key }) => {
    setSelectedDropDownItem(dropDownItems.find(
      item => item.slug === key,
    ) || dropDownItems[0]);
    setDropDownOpen(false);
  };

  const tabsItems = [{
    title: t(TRANSLATIONS.TOTAL_REPORTS),
    value: chartApiResponse?.totalReports.toLocaleString() || 0,
  }, {
    title: t(TRANSLATIONS.UNIQUE_USERS),
    value: chartApiResponse?.uniqueReporters?.toLocaleString() || 0,
  }, {
    title: t(TRANSLATIONS.AVERAGE_DAILY_PER_USER),
    value: chartApiResponse?.averageReportsByUser?.toLocaleString() || 0,
  }];
  const [selectedTab, setSelectedTab] = useState(tabsItems[0]);

  const getChartVars = () => {
    if (chartApiResponse === null) {
      return ({
        solidLineData: [],
        dottedLineData: [],
        averageLineData: [],
        labels: [],
      });
    }
    let seriesToConsider = null;
    let labelsToConsider = null;

    if (selectedTab.title === tabsItems[0].title) {
      seriesToConsider = chartApiResponse.incidentsByTime.data;
      labelsToConsider = chartApiResponse.incidentsByTime.labels;
    }
    if (selectedTab.title === tabsItems[1].title) {
      seriesToConsider = chartApiResponse.uniqueReportersByTime.data;
      labelsToConsider = chartApiResponse.uniqueReportersByTime.labels;
    }
    if (selectedTab.title === tabsItems[2].title) {
      seriesToConsider = chartApiResponse.avgPerUser.data;
      labelsToConsider = chartApiResponse.avgPerUser.labels;
    }

    if (seriesToConsider === null) {
      seriesToConsider = chartApiResponse.incidentsByTime.data.map(
        (d: number, i: number) => parseFloat((chartApiResponse.incidentsByTime.data[i]
          / chartApiResponse.uniqueReportersByTime.data[i]
        ).toFixed(2)),
      );
      labelsToConsider = chartApiResponse.incidentsByTime.labels;
    }

    const midOfSeries = Math.floor(seriesToConsider.length / 2);
    const solidLineData = seriesToConsider.slice(midOfSeries);
    const dottedLineData = seriesToConsider.slice(0, midOfSeries);
    const theLabels = labelsToConsider.slice(midOfSeries);
    const averageLinePos = (solidLineData.reduce(
      (acc: number, node: number) => acc + node,
      0,
    )) / solidLineData.length;
    const averageLinePoints = new Array(solidLineData.length).fill(averageLinePos.toFixed(2));
    return ({
      solidLineData,
      dottedLineData,
      averageLineData: averageLinePoints,
      labels: theLabels.map((l: string) => {
        return format(parseISO(l), 'd MMM');
      }),
    });
  };

  const chartVars = getChartVars();

  const chartData = {
    series: [{
      name: selectedDropDownItem.label,
      data: chartVars.solidLineData,
    },
    {
      name: t(TRANSLATIONS.PRECEDING_PERIOD),
      data: chartVars.dottedLineData,
    },
    {
      name: t(TRANSLATIONS.AVERAGE),
      data: chartVars.averageLineData,
    }],

    options: {
      colors: ['#70AD47', '#4472C4', '#F39C1F'],
      zoom: {
        enabled: true,
        autoScaleYaxis: true,
      },
      chart: {
        toolbar: {
          show: false,
        },
        height: 350,
        type: 'line',
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: [3, 3, 3],
        curve: 'straight',
        dashArray: [0, 4, 0],
      },
      markers: {
        size: 0,
        hover: {
          sizeOffset: 6,
        },
      },
      xaxis: {
        categories: chartVars.labels,
        labels: {
          rotate: 0,
          style: {
            // fontSize: '10px',
          },
        },
      },
      yaxis: {
        opposite: true,
        tickAmount: 4,
        decimalsInFloat: 0,
        forceNiceScale: true,
      },
      legend: {
        offsetY: 7,
      },
      tooltip: {
        y: [
          {
            title: {
              formatter(val: any) {
                return `${val}`;
              },
            },
          },
          {
            title: {
              formatter(val: any) {
                return `${val}`;
              },
            },
          },
        ],
      },
      grid: {
        borderColor: '#f1f1f1',
        padding: {
          top: 0,
          right: 25,
          bottom: 20,
          left: 25,
        },
      },
    },
  };

  const isEmpty = (!isLoading && !countResult?.series()[0]);

  if (isLoading || skip) return <LoadingSpinner height={231} />;

  return (
    <div className="overview-incidents-chart">
      <div
        className="tabs-container"
      >
        <ChartTabs
          tabsItems={tabsItems}
          setSelectedTab={setSelectedTab as any}
          selectedTab={selectedTab}
        />
        <div className="hc-details">
          <img
            src={userIcon}
            alt=""
            className="hc-icon"
          />
          <div>
            <p className="hc-header">HC</p>
            <p className="hc-value">{chartApiResponse?.totalUsers?.toLocaleString() || 0}</p>
          </div>
        </div>
      </div>
      <div
        style={{
          marginLeft: 15,
          marginRight: 15,
        }}
      >
        { isEmpty ? (
          <EmptyState />
        ) : (
          // @ts-ignore
          <Chart options={chartData.options} series={chartData.series} type="line" height={250} />
        ) }
        <ChartDropdown
          onChange={handleDropDownChange}
          selectedDropDownItem={selectedDropDownItem}
          dropDownItems={dropDownItems}
          dropDownOpen={dropDownOpen}
          setDropDownOpen={setDropDownOpen}
          labelClassName={`label-default ${isLoading ? 'dropdown-loading-labels' : 'dropdown-label-o'}`}
        />
      </div>
    </div>
  );
};

export default IncidentsChart;
